import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from '../../lib/icons';
import { useAuth } from '../../contexts/AuthContext';
import { setAdminToken } from '../../lib/image-slots-client';

const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.705 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function AdminLoginPage() {
  const { session, adminUser, loading, unauthorized, serviceUnavailable, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [meResult, setMeResult] = useState<string | null>(null);

  // Debug: test /api/admin/me directly when session exists but adminUser doesn't
  useEffect(() => {
    if (!loading && session && !adminUser && !unauthorized) {
      fetch('/api/admin/me', { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.json())
        .then(d => setMeResult(JSON.stringify(d)))
        .catch(e => setMeResult('fetch error: ' + e.message));
    }
  }, [loading, session, adminUser, unauthorized]);
  const [showPwForm, setShowPwForm] = useState(false);
  const [pw, setPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (!loading && session && adminUser) {
      navigate('/admin/edit-site', { replace: true });
    }
  }, [loading, session, adminUser, navigate]);

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    await signInWithGoogle();
    setGoogleLoading(false);
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!pw) return;
    setPwLoading(true);
    setPwErr('');
    try {
      const res = await fetch(`${API_BASE}/api/cms/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const data = await res.json() as { ok: boolean; token?: string; error?: string };
      if (data.ok && data.token) {
        sessionStorage.setItem('gfs_admin_auth', '1');
        setAdminToken(data.token);
        navigate('/admin/edit-site', { replace: true });
      } else {
        setPwErr(data.error ?? 'Incorrect password');
        setPw('');
      }
    } catch {
      setPwErr('Network error — check your connection');
    } finally {
      setPwLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF1744] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-3">
            <Shield className="h-8 w-8 text-[#FF1744]" />
          </div>
          <h1 className="text-[#E0E0E0] text-xl font-bold">GameFix Admin</h1>
          <p className="text-gray-500 text-xs mt-1">Authorized accounts only.</p>
        </div>

        {/* Temporary debug panel */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3 mb-4 text-xs text-gray-400 font-mono space-y-1">
          <div>loading: {String(loading)}</div>
          <div>session: {session ? '✓ ' + session.user.email : 'none'}</div>
          <div>adminUser: {adminUser ? '✓ ' + adminUser.role : 'none'}</div>
          <div>unauthorized: {String(unauthorized)}</div>
          {meResult && <div className="text-yellow-400 break-all">me: {meResult}</div>}
          <div className="text-gray-600 break-all">init: {sessionStorage.getItem('gfs_auth_init_url') ?? '—'}</div>
          {sessionStorage.getItem('gfs_auth_exchange_error') && (
            <div className="text-red-400 break-all">err: {sessionStorage.getItem('gfs_auth_exchange_error')}</div>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 space-y-4">

          {unauthorized && (
            <div className="bg-red-950/40 border border-red-800/50 rounded-xl px-4 py-3 text-center">
              <p className="text-red-400 text-sm">
                Your Google account is not authorized to access GameFix Admin.
              </p>
            </div>
          )}

          {serviceUnavailable && (
            <div className="bg-yellow-950/40 border border-yellow-700/50 rounded-xl px-4 py-3 text-center">
              <p className="text-yellow-400 text-sm">
                Admin authorization is temporarily unavailable. Your account may be authorized — please try again in a few minutes.
              </p>
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-white"
          >
            {googleLoading
              ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              : <GoogleIcon />}
            {googleLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>

          {!showPwForm ? (
            <button
              onClick={() => setShowPwForm(true)}
              className="w-full text-gray-600 hover:text-gray-400 text-xs py-1 transition-colors"
            >
              Use admin password instead
            </button>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-3 pt-2 border-t border-gray-800">
              <input
                type="password"
                value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="Admin password"
                autoFocus
                disabled={pwLoading}
                className="w-full bg-black/40 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 disabled:opacity-50 text-sm"
              />
              {pwErr && <p className="text-red-400 text-xs">{pwErr}</p>}
              <button
                type="submit"
                disabled={pwLoading || !pw}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50 text-sm"
              >
                {pwLoading ? 'Checking…' : 'Sign in with password'}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
